/**
 * PlayerAvatar
 *
 * A small round headshot that sits to the left of a player's name.
 *
 *   - `url` present: shows the picture. If it 404s (ESPN has no photo for a
 *     rookie yet, say) the <img> onError swaps to initials — no broken-image
 *     icon ever reaches the user.
 *   - no `url`: shows the player's initials on a soft navy disc. This is also
 *     what every row shows while the headshot batch is still loading, so
 *     rows never jump when the pictures arrive.
 *
 * Photos come from ESPN's public CDN; every page that renders this component
 * should also render <PhotoCredit /> once at the bottom.
 */
import { useMemo, useState } from 'react'
import { V2 } from './JaiV2'
import { JAI_FONTS } from './JaiBrand'
import useHeadshots from '../hooks/useHeadshots'

/** "Luka Doncic" -> "LD", "Jaren Jackson Jr." -> "JJ", "Giannis" -> "G" */
const initialsFor = (name) => {
  if (typeof name !== 'string') return ''
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p && !/^(jr|sr|ii|iii|iv|v)\.?$/i.test(p))
  if (parts.length === 0) return ''
  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return (first + last).toUpperCase()
}

/**
 * @param {string}  name   player display name (lookup key + initials)
 * @param {string}  sport  'nba' | 'nfl' (default 'nba')
 * @param {string}  [url]  explicit headshot url; when omitted the avatar
 *                         resolves one itself through useHeadshots (batched)
 * @param {number}  size   diameter in px (default 28)
 */
export default function PlayerAvatar({ name, sport = 'nba', url: explicitUrl, size = 28, style = {} }) {
  const names = useMemo(() => (explicitUrl || !name ? [] : [name]), [explicitUrl, name])
  const resolved = useHeadshots(sport, names)
  const url = explicitUrl || resolved[name] || null

  // Remember WHICH url failed, so a new url for the same slot (row reused for
  // a different player) gets a fresh chance to load without an effect.
  const [brokenUrl, setBrokenUrl] = useState(null)
  const broken = Boolean(url) && brokenUrl === url

  const showImg = Boolean(url) && !broken
  const box = {
    width: size,
    height: size,
    borderRadius: '50%',
    flexShrink: 0,
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: showImg ? V2.paper : 'rgba(10,31,68,0.08)',
    border: `1px solid ${V2.hairSoft}`,
    boxSizing: 'border-box',
    ...style,
  }

  if (showImg) {
    return (
      <span style={box} aria-hidden="true">
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setBrokenUrl(url)}
          // ESPN headshots are 200x145 with the face in the upper-middle;
          // cover + a slight downward focal point keeps chins in frame.
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: '50% 20%', display: 'block' }}
        />
      </span>
    )
  }

  return (
    <span style={box} aria-hidden="true">
      <span style={{
        fontSize: Math.max(9, Math.round(size * 0.36)),
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: V2.navy,
        fontFamily: JAI_FONTS.tight,
        lineHeight: 1,
        userSelect: 'none',
      }}>
        {initialsFor(name)}
      </span>
    </span>
  )
}

/**
 * One muted line at the foot of any page that shows headshots. The founder's
 * decision (10 Oct 2026) is to ship photos WITH an attribution, the way
 * League Winner NBA credits its source.
 */
export function PhotoCredit({ style = {} }) {
  return (
    <div style={{
      fontSize: 11,
      color: V2.faint,
      fontFamily: JAI_FONTS.tight,
      marginTop: 18,
      textAlign: 'right',
      ...style,
    }}>
      Photos: ESPN
    </div>
  )
}
